import { useMemo, useState } from 'react';
import { Ruler } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MEASUREMENT_SITES, avgWeightOn, cleanMeasurements, measurementSummary } from '../logic/measurements';
import { MeasureSheet } from '../body/MeasureSheet';
import { FONT_MONO } from '../theme';
import { Button, Card } from '../ui';

const signed = v => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}`);
const shortDate = k => new Date(k).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function Sparkline({ t, values }) {
  if (values.length < 2) return <span style={{ width: 60 }} />;
  const lo = Math.min(...values), hi = Math.max(...values);
  const span = hi - lo || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * 60},${18 - ((v - lo) / span) * 16}`).join(' ');
  return (
    <svg width="60" height="20" viewBox="0 0 60 20" aria-hidden="true" style={{ flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={t.accent} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

/* Tape measurements per site plus waist against the 7-day-average weight. */
export function BodyCard({ t, state, setState, today }) {
  const [measureOpen, setMeasureOpen] = useState(false);
  const rows = useMemo(() => cleanMeasurements(state.measurements), [state.measurements]);
  const summary = useMemo(() => measurementSummary(state, today), [state, today]);
  const chart = useMemo(() => rows.filter(m => m.waist != null).map(m => ({
    label: shortDate(m.date), waist: m.waist, weight: avgWeightOn(state.bodyMetrics, m.date),
  })), [rows, state.bodyMetrics]);

  return (
    <Card t={t} padding={20}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Body</div>
        {setState && (
          <Button t={t} size="sm" variant="ghost" onClick={() => setMeasureOpen(true)} style={{ minHeight: 40 }}>
            <Ruler size={13} /> Measure
          </Button>
        )}
      </div>
      {!rows.length ? (
        <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.45 }}>
          No measurements yet. Waist every two weeks next to your weigh-ins shows what the scale can't.
        </div>
      ) : (
        <>
          {summary.sentence && <div style={{ fontSize: 13, color: t.text, marginBottom: 10, lineHeight: 1.45 }}>{summary.sentence}</div>}
          {summary.due && (
            <div style={{ fontSize: 11, color: t.textFaint, marginBottom: 8 }}>Last tape {summary.daysSince} days ago: time to measure.</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', columnGap: 10, rowGap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: t.textFaint, fontWeight: 700 }}>SITE</span>
            <span style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, textAlign: 'right' }}>CM</span>
            <span style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, textAlign: 'right' }}>4 WK</span>
            <span style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, textAlign: 'right' }}>ALL</span>
            {MEASUREMENT_SITES.filter(s => summary.sites[s.id]).map(s => {
              const st = summary.sites[s.id];
              return [
                <span key={`${s.id}-l`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: t.text, minWidth: 0 }}>
                  {s.label}
                  <Sparkline t={t} values={rows.filter(m => m[s.id] != null).map(m => m[s.id])} />
                </span>,
                <span key={`${s.id}-v`} style={{ fontSize: 13, fontFamily: FONT_MONO, fontWeight: 700, color: t.text, textAlign: 'right' }}>{st.latest}</span>,
                <span key={`${s.id}-4`} style={{ fontSize: 12, fontFamily: FONT_MONO, color: t.textMuted, textAlign: 'right' }}>{signed(st.change4w)}</span>,
                <span key={`${s.id}-a`} style={{ fontSize: 12, fontFamily: FONT_MONO, color: t.textMuted, textAlign: 'right' }}>{signed(st.sinceFirst)}</span>,
              ];
            })}
          </div>
          {chart.length > 1 && (
            <div style={{ height: 170, marginTop: 14 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.divider} vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} />
                  <YAxis yAxisId="w" domain={['dataMin - 1', 'dataMax + 1']} tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} width={36} />
                  <YAxis yAxisId="c" orientation="right" domain={['dataMin - 1', 'dataMax + 1']} tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} width={36} />
                  <Tooltip contentStyle={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: t.text }} itemStyle={{ color: t.text }} />
                  <Line yAxisId="w" type="monotone" dataKey="weight" name="Weight 7d avg (kg)" stroke={t.textFaint} strokeWidth={2} dot={false} connectNulls />
                  <Line yAxisId="c" type="monotone" dataKey="waist" name="Waist (cm)" stroke={t.accent} strokeWidth={2.5} dot />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
      {measureOpen && <MeasureSheet t={t} state={state} setState={setState} dayKey={today} onClose={() => setMeasureOpen(false)} />}
    </Card>
  );
}
